/**
 * Milestone Utilities
 * Functions for creating and managing default milestones
 */

/**
 * Calculate date that is X days before a given date
 * @param {string|Date} baseDate - The base date (typically closing date)
 * @param {number} daysOffset - Number of days before (positive number)
 * @returns {Date|null} - The calculated date, or null if baseDate is invalid
 */
export const calculateDaysBefore = (baseDate, daysOffset) => {
  if (!baseDate) return null;

  try {
    const date = new Date(baseDate);
    if (isNaN(date.getTime())) return null;

    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() - daysOffset);
    return newDate;
  } catch (error) {
    console.error('Error calculating days before:', error);
    return null;
  }
};

/**
 * Format date for datetime-local input (YYYY-MM-DDTHH:MM)
 * @param {string|Date} date - The date to format
 * @returns {string} - Formatted date string for datetime-local input
 */
export const formatDateForInput = (date) => {
  if (!date) return '';

  try {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Generate default milestones based on closing date
 * Currently generates:
 * 1. Schedule Final Walkthrough (7 days before closing)
 * 2. Change Utilities (7 days before closing)
 *
 * @param {string|Date} closingDate - The closing date from contract
 * @param {string} dealId - The deal ID for the milestone
 * @returns {Array} - Array of default milestone objects
 */
export const generateDefaultMilestones = (closingDate, dealId) => {
  const milestones = [];

  if (!closingDate || !dealId) {
    return milestones;
  }

  // Calculate 7 days before closing
  const sevenDaysBeforeClosing = calculateDaysBefore(closingDate, 7);

  if (!sevenDaysBeforeClosing) {
    return milestones;
  }

  const dateString = sevenDaysBeforeClosing.toISOString();

  // Milestone 1: Schedule Final Walkthrough
  milestones.push({
    id: `default-walkthrough-${Date.now()}`,
    title: 'Schedule Final Walkthrough',
    description: 'Final inspection of property condition before closing',
    start_dt: dateString,
    end_dt: dateString,
    all_day: true,
    type: 'default',
    status: 'active',
    deal_id: dealId,
    isDefault: true // Flag to identify auto-generated milestones
  });

  // Milestone 2: Change Utilities
  milestones.push({
    id: `default-utilities-${Date.now()}`,
    title: 'Change Utilities',
    description: 'Transfer utilities to take effect on closing day. The actual utility change takes effect ON closing day, but start this process 1 week before.',
    start_dt: dateString,
    end_dt: dateString,
    all_day: true,
    type: 'default',
    status: 'active',
    deal_id: dealId,
    isDefault: true // Flag to identify auto-generated milestones
  });

  return milestones;
};

/**
 * Check if a milestone is a default milestone
 * @param {Object} milestone - The milestone object to check
 * @returns {boolean} - True if milestone is a default one
 */
export const isDefaultMilestone = (milestone) => {
  return milestone?.isDefault === true || milestone?.type === 'default';
};

/**
 * Filter out default milestones from a milestone array
 * @param {Array} milestones - Array of milestones
 * @returns {Array} - Array with only non-default milestones
 */
export const filterOutDefaultMilestones = (milestones) => {
  return milestones.filter(m => !isDefaultMilestone(m));
};

/**
 * Recalculate default milestones when closing date changes
 * @param {Array} milestones - Current milestones array
 * @param {string|Date} newClosingDate - The new closing date
 * @param {string} dealId - The deal ID
 * @returns {Array} - Updated milestones array with recalculated defaults
 */
export const updateDefaultMilestonesIfNeeded = (milestones, newClosingDate, dealId) => {
  if (!newClosingDate || !dealId) {
    return milestones;
  }

  // Remove old default milestones
  const nonDefaultMilestones = filterOutDefaultMilestones(milestones);

  // Generate new default milestones
  const newDefaults = generateDefaultMilestones(newClosingDate, dealId);

  // Combine and return
  return [...nonDefaultMilestones, ...newDefaults];
};
